import { Divider, Stack, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  addMinutes,
  differenceInMinutes,
  differenceInWeeks,
  format,
  startOfToday,
} from "date-fns";
import React, { useEffect } from "react";
import TimeSelectorDatePicker from "@/components/booking/timeSelector/datePicker";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import InfoCard from "@/components/common/infoCard/infoCard";
import { TIME_FORMATS } from "@/config";
import { formattedTime } from "@/utils/date";
import useErrorLogger from "@/utils/useErrorLogger";
import { AppointmentServiceTimeSelector } from "./appointmentServiceTimeSelector";
import { AppointmentServiceInput } from "./multiProviderAppointmentDrawerContext";

type Props = {
  date: Date;
  appointmentServices: AppointmentServiceInput[];
  isRebooking: boolean;
  onDateChange: (date: Date) => void;
  onAppointmentTimeChange: (options: {
    newStartTime: Date;
    newEndTime: Date;
    index: number;
  }) => void;
};

export const TimeSelectionForm = ({
  date,
  appointmentServices,
  isRebooking,
  onDateChange,
  onAppointmentTimeChange,
}: Props) => {
  const logError = useErrorLogger();
  useEffect(() => {
    if (!appointmentServices.length) {
      logError(new Error("[TimeSelectionForm] No appointment services."));
    }
  }, [appointmentServices, logError]);

  if (!appointmentServices.length) {
    return null;
  }
  const appointmentStartTime = appointmentServices[0].startTime;
  const appointmentEndTime = appointmentServices.at(-1).endTime;
  const appointmentTotalDuration = getAppointmentTotalDuration(
    appointmentStartTime,
    appointmentEndTime
  );

  const today = startOfToday();
  const weeksDifference = differenceInWeeks(date, today, {
    roundingMethod: "ceil",
  });

  return (
    <InfoCard
      sx={{
        mb: 2,
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack gap={2}>
          <TimeSelectorDatePicker
            value={date}
            onDateChange={onDateChange}
            fullWidth
            sx={{
              "& .MuiInputBase-input": {
                height: "44px",
                padding: "9px 12px",
                boxSizing: "border-box",
              },
            }}
          />
          <Stack gap={1.5}>
            {appointmentServices.map(
              ({ startTime, endTime, serviceMenuItem, provider }, index) => (
                <AppointmentServiceTimeSelector
                  key={serviceMenuItem.id}
                  provider={provider}
                  serviceMenuItem={serviceMenuItem}
                  startTime={startTime}
                  minStartTime={
                    index > 0 ? appointmentServices[index - 1].endTime : null
                  }
                  endTime={endTime}
                  onStartTimeChange={(newStartTime) => {
                    const startTimeShift = differenceInMinutes(
                      newStartTime,
                      startTime
                    );
                    const newEndTime = addMinutes(endTime, startTimeShift);
                    onAppointmentTimeChange({
                      newStartTime,
                      newEndTime,
                      index,
                    });
                  }}
                  onEndTimeChange={(newEndTime) =>
                    onAppointmentTimeChange({
                      newStartTime: startTime,
                      newEndTime,
                      index,
                    })
                  }
                />
              )
            )}
          </Stack>
          <Stack gap={1.5}>
            <Divider />
            <Stack gap={1.5} direction="row" justifyContent="space-between">
              <Typography variant="labelSmall" color="text.primary">
                Appt will take <strong>{appointmentTotalDuration}</strong>
              </Typography>
              <Typography variant="labelSmall" color="text.primary">
                {format(appointmentStartTime, TIME_FORMATS.COMPACT_SPACE)} -{" "}
                {format(appointmentEndTime, TIME_FORMATS.COMPACT_SPACE)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </LocalizationProvider>
      {isRebooking ? (
        <AdditionalInfo
          variant="info"
          text={
            <>
              Selected date is <strong>{weeksDifference} weeks</strong> from
              today
            </>
          }
          sx={{ my: 1 }}
        />
      ) : null}
    </InfoCard>
  );
};

const getAppointmentTotalDuration = (
  appointmentStartTime: Date,
  appointmentEndTime: Date
) =>
  formattedTime(
    differenceInMinutes(appointmentEndTime, appointmentStartTime)
  ).replace(/(\d)\s+([a-zA-Z])/g, "$1$2"); // Replace spaces between a digit and a letter
